import { useState } from 'react'
import { isAfter } from 'date-fns'
import { GoogleAuth } from '../services/auth/google/google'
import Api from '@vacationtracker/shared/services/api'
import logger from '@vacationtracker/shared/functions/logger'

const googleAuth = new GoogleAuth(process.env.REACT_APP_GOOGLE_CLIENT_ID)

export interface IUseGoogleAutoImportValidationCallbacks {
  setShowGoogleLoginRequiredModal: (show: boolean) => void
  setShowGoogleInsufficientPermissionsModal: (show: boolean) => void
  onValidationFail: () => void
  onUnexpectedError?: (error: Error) => void
}

export interface IUseGoogleAutoImportValidationResult {
  isValidatingGooglePermissions: boolean
  adminTokenHash: string | null
  pendingAutoImportValidation: boolean
  setPendingAutoImportValidation: (pending: boolean) => void
  setAdminTokenHash: (hash: string | null) => void
  checkAutoImportAutomaticallyGoogle: (value: boolean) => Promise<boolean>
}

export const useGoogleAutoImportValidation = (
  callbacks: IUseGoogleAutoImportValidationCallbacks
): IUseGoogleAutoImportValidationResult => {
  const [isValidatingGooglePermissions, setIsValidatingGooglePermissions] = useState(false)
  const [adminTokenHash, setAdminTokenHash] = useState<string | null>(
    sessionStorage.getItem('adminTokenHash')
  )
  const [pendingAutoImportValidation, setPendingAutoImportValidation] = useState(false)

  const {
    setShowGoogleLoginRequiredModal,
    setShowGoogleInsufficientPermissionsModal,
    onValidationFail,
    onUnexpectedError,
  } = callbacks

  const checkAutoImportAutomaticallyGoogle = async (value: boolean): Promise<boolean> => {
    if (!value) return true

    // Check if token exists and is not expired
    const googleTokenExpiration = sessionStorage.getItem('googleTokenExpiration')
    if (!googleTokenExpiration || isAfter(new Date(), new Date(googleTokenExpiration))) {
      setPendingAutoImportValidation(true)
      setShowGoogleLoginRequiredModal(true)
      onValidationFail()
      return false
    }

    // Check if Google session is available
    // Note: getAccessToken() must be called first as it populates this.tokens from session storage
    let accessToken: string
    let userInfo: { hd?: string }
    try {
      accessToken = googleAuth.getAccessToken()
      userInfo = googleAuth.getSignedInUser()
    } catch {
      setPendingAutoImportValidation(true)
      setShowGoogleLoginRequiredModal(true)
      onValidationFail()
      return false
    }
    if (!userInfo.hd) {
      setPendingAutoImportValidation(true)
      setShowGoogleLoginRequiredModal(true)
      onValidationFail()
      return false
    }

    // Validate that the user has actual Admin Directory API permissions
    setIsValidatingGooglePermissions(true)
    try {
      await Api.post('/google/validate-auto-import', {
        accessToken,
        domain: userInfo.hd,
      })
      // Validation passed - user has Super Admin permissions
      logger.info('Google auto-import validation passed')
      return true
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { error?: string } }; message?: string }
      logger.error('Google auto-import validation failed', error)

      const isInsufficientPermissions =
        err.response?.status === 403 ||
        err.response?.data?.error === 'INSUFFICIENT_ADMIN_PERMISSIONS'

      if (isInsufficientPermissions) {
        // User doesn't have Super Admin permissions - try to create delegation hash
        try {
          let hash = sessionStorage.getItem('adminTokenHash')
          if (!hash) {
            const response = await Api.post('/google/admin-token-hash', {}) as { hash: string }
            hash = response.hash
            sessionStorage.setItem('adminTokenHash', hash)
          }
          setAdminTokenHash(hash)
          logger.info('Admin token hash created/retrieved', { hashLength: hash?.length })
        } catch (hashError) {
          logger.error('Failed to create admin token hash', hashError)
          // Continue anyway - show modal without link
        }
        setShowGoogleInsufficientPermissionsModal(true)
        onValidationFail()
      } else if (err.response?.status === 401) {
        // Token expired
        setShowGoogleLoginRequiredModal(true)
        onValidationFail()
      } else {
        // Unexpected error
        if (onUnexpectedError) {
          onUnexpectedError(error as Error)
        }
        onValidationFail()
      }
      return false
    } finally {
      setIsValidatingGooglePermissions(false)
    }
  }

  return {
    isValidatingGooglePermissions,
    adminTokenHash,
    pendingAutoImportValidation,
    setPendingAutoImportValidation,
    setAdminTokenHash,
    checkAutoImportAutomaticallyGoogle,
  }
}
